// client/src/components/pages/LexiStar/Declarations/DeclarationForm.jsx
//
// Declare one condition across many trips (LS-09 §5.21; T4-S14):
//   effect → scope (routes, lanes, a road segment drawn on the map, sites,
//   trucks, vehicle class) → period (past or future, the shared date-time
//   picker) → reason → optional alternate corridor → Preview → Publish.
// Preview creates the draft (POST /declarations) and counts what falls inside
// it (POST /declarations/:id:preview: trips, deviations, delay hours) before
// anything is published. Changing the form after a preview makes the preview
// stale — the next preview creates a fresh draft. Publishing a past or
// spanning period queues a recompute that a second user publishes.
// Security and risk events are never touched by a declaration.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEye, FaBullhorn, FaShieldAlt } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import UnifiedDateTimeRangePicker from '../../../common/UnifiedDateTimeRangePicker';
import {
  createDeclaration,
  previewDeclaration,
  publishDeclaration,
  pickObject,
} from '../../../../services/lexistarAssurance';
import { useReasonCodes, ReasonCodeSelect, inputClasses, cardClasses } from '../StatusBoard/atoms';
import { fmtNum } from '../StatusBoard/format';
import useRouteGeometries from '../Approvals/useRouteGeometries';
import {
  Field,
  Modal,
  InlineError,
  ReasonField,
  buttonClasses,
} from '../ReviewQueue/assuranceAtoms';
import { errorMessage, localToIso, toLocalValue } from '../ReviewQueue/assuranceShared';
import {
  SCOPE_KINDS,
  EFFECTS,
  DEFAULT_CORRIDOR_M,
  TIMING_TEXT,
  timing,
  validateDeclaration,
  buildDeclarationPayload,
  effectMeta,
  effectSentence,
} from './declarationLogic';
import SegmentDrawMap from './SegmentDrawMap';

const HOUR = 3600000;

export const SECURITY_NOTE = 'Security and risk events are not affected.';

export default function DeclarationForm({ reference, onCancel, onPublished }) {
  const now = useMemo(() => new Date(), []);
  const [effect, setEffect] = useState('deviation_approved');
  const [scopeKind, setScopeKind] = useState('route');
  const [ids, setIds] = useState([]);
  const [segment, setSegment] = useState([]);
  const [corridorM, setCorridorM] = useState(DEFAULT_CORRIDOR_M);
  const [start, setStart] = useState(toLocalValue(now));
  const [end, setEnd] = useState(toLocalValue(new Date(now.getTime() + 12 * HOUR)));
  const [reasonCode, setReasonCode] = useState('');
  const [reason, setReason] = useState('');
  const [withAlt, setWithAlt] = useState(false);
  const [altLine, setAltLine] = useState([]);
  const [drawing, setDrawing] = useState('segment');
  const [traceRoute, setTraceRoute] = useState('');
  const [draft, setDraft] = useState(null); // { id, key, preview }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const codes = useReasonCodes(effectMeta(effect).reasonKind || 'deviation');
  const { lines: traceLines } = useRouteGeometries(
    traceRoute
      ? [{ route_id: traceRoute, route_version: reference.routeVersions[traceRoute] ?? 1 }]
      : [],
    { corridor: false }
  );

  useEffect(() => {
    setIds([]);
    if (scopeKind === 'corridor_segment') setDrawing('segment');
  }, [scopeKind]);
  useEffect(() => {
    setReasonCode('');
  }, [effect]);

  const form = {
    effect,
    scopeKind,
    ids,
    segment,
    corridorM,
    from: localToIso(start),
    to: localToIso(end),
    reasonCode,
    reason,
    withAlt,
    altLine,
    altCorridorM: corridorM,
  };
  const payload = buildDeclarationPayload(form);
  const key = JSON.stringify(payload);
  const previewCurrent = draft && draft.key === key && draft.preview;
  const when = timing(form.from, form.to);
  const showMap = scopeKind === 'corridor_segment' || withAlt;

  const preview = async () => {
    const problem = validateDeclaration(form);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      let id = draft && draft.key === key ? draft.id : null;
      if (!id) {
        const created = pickObject(await createDeclaration(payload), 'declaration');
        id = created?.id;
      }
      const res = await previewDeclaration(id);
      const p = res?.preview || pickObject(res, 'declaration')?.preview || res;
      setDraft({ id, key, preview: p });
    } catch (err) {
      setError(errorMessage(err, 'The preview failed; nothing was published.'));
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await publishDeclaration(draft.id);
      const d = pickObject(res, 'declaration');
      const jobId = res?.recompute_job_id || res?.recompute_job?.id || d?.recompute_job_id || null;
      setConfirmOpen(false);
      onPublished?.({ declaration: d, recomputeJobId: jobId, when });
    } catch (err) {
      setError(errorMessage(err, 'The declaration was not published.'));
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const scopeMeta = SCOPE_KINDS.find((k) => k.value === scopeKind);
  const scopeOptions = reference.options[scopeKind] || [];

  return (
    <section className={`${cardClasses} p-4 sm:p-5`} aria-label="New declaration">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">New declaration</h2>
          <p className="text-sm text-slate-600">
            One condition, declared once, across every trip it touches.
          </p>
        </div>
        <button type="button" onClick={onCancel} className={buttonClasses.secondary}>
          Cancel
        </button>
      </div>

      <div
        className={`grid grid-cols-1 ${showMap ? 'xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]' : ''} gap-5`}
      >
        <div className={`space-y-4 ${showMap ? '' : 'max-w-3xl'}`}>
          <fieldset>
            <legend className="block text-sm font-medium text-slate-700 mb-1.5">Effect</legend>
            <div className="grid grid-cols-1 gap-2">
              {EFFECTS.map((e) => (
                <label
                  key={e.value}
                  className={`flex gap-3 p-3 rounded-lg border cursor-pointer ${effect === e.value ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:bg-slate-50'}`}
                >
                  <input
                    type="radio"
                    name="decl-effect"
                    value={e.value}
                    checked={effect === e.value}
                    onChange={() => setEffect(e.value)}
                    className="mt-1"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">{e.label}</span>
                    <span className="block text-xs text-slate-600">{e.help}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <Field label="Scope" htmlFor="decl-scope-kind" hint={scopeMeta?.help}>
            <ThemedSelect
              id="decl-scope-kind"
              value={scopeKind}
              onChange={(e) => setScopeKind(e.target.value)}
              aria-label="Scope"
            >
              {SCOPE_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </ThemedSelect>
          </Field>

          {scopeKind === 'corridor_segment' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label="Corridor width (m)"
                htmlFor="decl-corridor-m"
                hint="Full width; half each side of the line."
              >
                <input
                  id="decl-corridor-m"
                  type="number"
                  min="50"
                  max="5000"
                  step="50"
                  value={corridorM}
                  onChange={(e) => setCorridorM(e.target.value)}
                  className={inputClasses}
                />
              </Field>
              <Field label="Trace along a route (optional)" htmlFor="decl-trace">
                <ThemedSelect
                  id="decl-trace"
                  value={traceRoute}
                  onChange={(e) => setTraceRoute(e.target.value)}
                  aria-label="Trace along a route"
                >
                  <option value="">No reference route</option>
                  {reference.options.route.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </ThemedSelect>
              </Field>
            </div>
          ) : (
            <Field
              label={scopeMeta?.label}
              hint={scopeKind === 'fleet' ? 'Leave empty for the whole fleet.' : undefined}
            >
              <ThemedSelect
                isMulti
                value={ids}
                onChange={(e) => setIds(e.target.value)}
                aria-label={`Choose ${scopeMeta?.label?.toLowerCase()}`}
                placeholder={
                  scopeOptions.length
                    ? `Choose ${scopeMeta?.label?.toLowerCase()}…`
                    : reference.loaded
                      ? 'Nothing to choose from'
                      : 'Loading…'
                }
              >
                {scopeOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </ThemedSelect>
            </Field>
          )}

          <Field
            label="Period"
            hint={
              when ? TIMING_TEXT[when] : 'Past or future; the date-time range the condition held.'
            }
          >
            <UnifiedDateTimeRangePicker
              startValue={start}
              endValue={end}
              onStartChange={setStart}
              onEndChange={setEnd}
              placeholder="Choose the period"
            />
          </Field>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Reason code <span className="font-normal text-slate-500">(optional)</span>
            </label>
            <ReasonCodeSelect
              codes={codes}
              value={reasonCode}
              onChange={setReasonCode}
              label="Reason code"
            />
          </div>
          <ReasonField
            value={reason}
            onChange={setReason}
            placeholder="N4 closed at Middelburg for a protest — trucks diverted via the R555"
          />

          <label className="flex items-center gap-2 text-sm text-slate-700 min-h-[36px]">
            <input
              type="checkbox"
              checked={withAlt}
              onChange={(e) => {
                setWithAlt(e.target.checked);
                if (e.target.checked) setDrawing('alt');
                else {
                  setAltLine([]);
                  setDrawing(scopeKind === 'corridor_segment' ? 'segment' : null);
                }
              }}
            />
            Alternate corridor — trips inside it count as compliant while this holds
          </label>

          <p className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <FaShieldAlt className="mt-0.5 flex-shrink-0 text-slate-500" aria-hidden="true" />
            {SECURITY_NOTE} A declaration changes how deviations, delays and stops are classified;
            it never suppresses a security or risk event.
          </p>
        </div>

        {showMap && (
          <div className="space-y-2">
            {scopeKind === 'corridor_segment' && withAlt && (
              <div role="group" aria-label="What to draw" className="flex gap-2">
                {[
                  ['segment', 'Draw the segment'],
                  ['alt', 'Draw the alternate'],
                ].map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={drawing === v}
                    onClick={() => setDrawing(v)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${drawing === v ? 'bg-slate-900 text-white border-slate-900' : 'bg-surface text-slate-700 border-slate-300'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <SegmentDrawMap
              segment={segment}
              onSegmentChange={setSegment}
              corridorM={corridorM}
              altLine={altLine}
              onAltChange={setAltLine}
              altCorridorM={corridorM}
              drawing={drawing}
              referenceLines={traceLines}
            />
          </div>
        )}
      </div>

      <div className="mt-5 pt-4 border-t border-slate-200 space-y-3">
        {previewCurrent ? (
          <div
            role="status"
            aria-label="Preview"
            className="rounded-lg border border-slate-300 bg-slate-50 px-4 py-3"
          >
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              In scope
            </p>
            <p className="mt-1 text-lg font-bold text-slate-900 tabular-nums">
              {effectSentence(draft.preview, effect) || `${fmtNum(draft.preview.trips)} trips`}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Counted now over the period and scope above. {when ? TIMING_TEXT[when] : ''}
            </p>
          </div>
        ) : draft ? (
          <p className="text-sm text-slate-600">
            The form changed since the preview — preview again before publishing.
          </p>
        ) : null}
        <InlineError message={error} />
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={preview}
            disabled={busy}
            className={buttonClasses.secondary}
          >
            <FaEye size={12} />{' '}
            {busy && !previewCurrent
              ? 'Counting…'
              : previewCurrent
                ? 'Preview again'
                : 'Preview affected trips'}
          </button>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={busy || !previewCurrent}
            className={buttonClasses.primary}
          >
            <FaBullhorn size={12} /> Publish
          </button>
        </div>
      </div>

      <Modal
        open={confirmOpen}
        title="Publish this declaration"
        onClose={() => setConfirmOpen(false)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className={buttonClasses.secondary}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={publish}
              disabled={busy}
              className={buttonClasses.primary}
            >
              {busy ? 'Publishing…' : 'Publish'}
            </button>
          </>
        }
      >
        <p className="text-sm text-slate-800 font-semibold">{effectMeta(effect).label}</p>
        {previewCurrent && (
          <p className="text-sm text-slate-700 tabular-nums">
            {effectSentence(draft.preview, effect)}
          </p>
        )}
        <p className="text-sm text-slate-600">{when ? TIMING_TEXT[when] : ''}</p>
        <p className="text-sm text-slate-600">{SECURITY_NOTE}</p>
        {(when === 'past' || when === 'spanning') && (
          <p className="text-xs text-slate-500">
            The recompute appears under{' '}
            <Link to="/lexistar/recompute-jobs" className="underline">
              Recompute jobs
            </Link>
            .
          </p>
        )}
      </Modal>
    </section>
  );
}
